/**
 * Fine-grained page/menu permission helpers.
 *
 * `user.auth_menus` is the same per-user menu tree the left sidebar
 * (components/layouts/drawerLeft.vue) renders from, already scoped by the
 * backend to whatever an admin/owner granted that user on the
 * settings > permission page. It is the real source of truth for
 * "what is this specific user allowed to open" — the numeric
 * `meta.status` role check in the router only tells us what a whole role
 * (owner/manager/cashier/waiter) can broadly reach.
 */

// Routes not covered by the per-user menu permission tree — core
// operational screens every authenticated, role-permitted user can reach
// regardless of what an admin did/didn't tick on the permission page.
// Never gate these by auth_menus: several of them (e.g. "/sale") are also
// the guard's own fallback redirect target, so blocking them would loop.
export const PERMISSION_EXEMPT_PATHS = new Set([
  "/sale",
  "/table",
  "/mobile/table",
  "/kitchen",
  "/customer-screen",
  "/dashboard",
  "/price-checker",
  "/variations-demo",

  // Pages with no menu row anywhere. Nothing in the live menu table points at
  // these, so they can never appear in anyone's auth_menus, no admin can grant
  // them on the permission page, and the check below could only ever fail
  // closed — it would deny every non-admin rather than enforce anything. They
  // are top-level paths, so there is no granted ancestor to inherit from
  // either. Role (meta.status) is the only gate they have ever had.
  //
  // /open-store is the honest case: it is pushed straight from login, so no
  // permission could sensibly gate it. Anything else here is a page Top has
  // not yet named an owning menu for — prefer INHERITED_MENU_PARENTS once
  // they do, since exempt means "not permissionable at all".
  //
  // tests/unit/routePermissionCoverage.test.js fails if a new route lands
  // here without a menu row and without being listed.
  "/open-store",
]);

// Pages that belong to a menu section but sit at a top-level path instead of
// under it, so the ancestor walk in hasMenuAccess() can't find their parent.
// Do not map paths already covered by an ancestor menu: doing so can grant
// access based on an unrelated menu and override the user's actual grant.
// Each maps to the menu row whose permission governs it. Prefer this over
// PERMISSION_EXEMPT_PATHS whenever a page really is governed by some menu the
// admin can tick — exempt means "role only, not permissionable at all".
//
// This map answers "which AREA is this page part of", not "may you change
// anything in it". QRIPO-416 originally read the Ingredients entry below as
// both — granting that menu was taken to be what let a user restock — but the
// API never agreed: IngredientRestockController::store has always required
// manage_product_recipe, so a user holding only the menu could open the
// restock form and then be refused on save. QRIPO-472 settled it the other
// way round, per the API: the menu opens the area, and meta.permissionCode on
// the individual route decides whether you may write (see authGuard.js).
export const INHERITED_MENU_PARENTS = new Map([
  ["/report-product", "/report-category"],
  ["/ingredient-restock", "/manage-ingredients"],
  ["/trash-products", "/products"],
  ["/manage-stock", "/products"],
  ["/cash-drawers", "/cash-management"],
  ["/printers", "/setting"],
  ["/documents", "/setting"],
  ["/overdue", "/report-bill"],
]);

function normalizePath(path) {
  if (!path) return path;
  return path.length > 1 && path.endsWith("/") ? path.slice(0, -1) : path;
}

function isActive(item) {
  return item.is_active === undefined || Number(item.is_active) === 1;
}

/** Flattens a user's auth_menus tree into the set of route paths they may open. */
export function flattenMenuPaths(menus) {
  const paths = new Set();
  const visit = (item) => {
    if (!item) return;
    if (isActive(item) && item.to) {
      paths.add(normalizePath(item.to));
    }
    if (item.menu && item.menu.length) {
      item.menu.forEach(visit);
    }
  };
  (menus || []).forEach(visit);
  return paths;
}

/**
 * Whether `route` should be checked against the user's auth_menus at all.
 * Only the shared, admin-configurable page pool qualifies: it must require
 * auth, be reachable by more than just admins (admin-only routes are
 * already fully gated by role status), have a static path (dynamic
 * detail/sub-pages inherit their parent list page's access through normal
 * in-app navigation rather than being individually permissioned), and not
 * be explicitly exempted.
 */
export function isPermissionCheckedRoute(route) {
  const meta = route.meta || {};
  // Use the matched route *record*'s path, not `route.path`: on a resolved
  // Route, `path` is the concrete URL ("/manage-edit-product/12"), so the
  // dynamic-segment test below would never fire and every detail page would
  // be checked against — and missing from — auth_menus.
  const matched = route.matched || [];
  const record = matched.length ? matched[matched.length - 1] : null;
  const path = (record && record.path) || route.path || "";
  if (meta.permissionExempt) return false;
  if (!meta.requiresAuth) return false;
  if (!meta.status || !meta.status.length) return false;
  if (meta.status.length === 1 && meta.status[0] === 1) return false;
  if (path.includes(":")) return false;
  if (PERMISSION_EXEMPT_PATHS.has(normalizePath(path))) return false;
  return true;
}

/**
 * Whether any ancestor of `path` is in `granted` — "/a/b/c" tries "/a/b",
 * then "/a". Stops before the bare "/" root, which is never a menu entry.
 */
function hasGrantedAncestor(granted, path) {
  let cursor = path || "";
  for (;;) {
    const cut = cursor.lastIndexOf("/");
    if (cut <= 0) return false;
    cursor = cursor.slice(0, cut);
    if (granted.has(cursor)) return true;
  }
}

/**
 * Whether `user` may open `path` per their own auth_menus.
 *
 * Missing/undefined auth_menus means the backend hasn't sent permission
 * data for this account — fail OPEN (don't block) rather than lock a real
 * user out of everything. An auth_menus array that is present but simply
 * omits the path means it was actively not granted — fail CLOSED.
 *
 * A sub-page inherits access from its granted parent. auth_menus only ever
 * carries list-page paths — MenuSeeder has a row for "/manage-promotions"
 * and none for its "/manage-promotions/new" add form, and never will — so
 * requiring an exact match would bounce every user who legitimately holds
 * the parent menu. isPermissionCheckedRoute() already skips *dynamic*
 * sub-pages by their ":"; the ancestor walk is what covers the static ones,
 * and INHERITED_MENU_PARENTS covers the ones that sit at a top-level path
 * despite belonging to a section. A top-level page the user wasn't granted
 * has nothing to inherit from and is still blocked.
 */
export function hasMenuAccess(user, path) {
  const menus = user?.auth_menus;
  if (!menus) return true;
  const granted = flattenMenuPaths(menus);
  const target = normalizePath(path);
  if (granted.has(target) || hasGrantedAncestor(granted, target)) {
    return true;
  }
  const parent = INHERITED_MENU_PARENTS.get(target);
  return parent ? granted.has(parent) : false;
}

/**
 * Whether `user` may perform an action gated on menu code `code`.
 *
 * Follows the same rule hasMenuAccess() does: auth_menus missing entirely
 * means the backend sent no permission data for this account — fail OPEN, and
 * let the API have the final say — while an auth_menus that is present and
 * simply omits the code means it was actively not granted, so fail CLOSED.
 *
 * Use this, not hasPermissionCode(), wherever a missing grant should HIDE or
 * BLOCK something: the two disagree exactly on the no-auth_menus case, and
 * routing and rendering must not disagree about the same user.
 */
export function hasActionPermission(user, code) {
  if (!user || !user.auth_menus) return true;
  // Report is represented by granted report routes in auth_menus, not
  // necessarily by a menu whose code is literally "report". Stock Report
  // shares Product Report's existing inherited menu permission.
  if (code === "report" && hasMenuAccess(user, "/report-product")) return true;
  return hasPermissionCode(user, code);
}

/** Whether `user` has been granted menu item `code` anywhere in their auth_menus tree. */
export function hasPermissionCode(user, code) {
  const menus = user?.auth_menus;
  if (!menus || !code) return false;
  let found = false;
  const visit = (item) => {
    if (found || !item) return;
    if (item.code === code) {
      found = true;
      return;
    }
    if (item.menu && item.menu.length) {
      item.menu.forEach(visit);
    }
  };
  menus.forEach(visit);
  return found;
}
